import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, Play, Gauge, Repeat, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { getScenario, createSession } from "@/lib/api";

const LEVELS = ["basic", "intermediate", "advanced"];

// Simplified role labels for the learner-facing Briefing card. Keyed by the
// scenario's `partner` (customer|guest|patient|colleague|supervisor|caller|
// vendor|resident|...). Falls back to the learner's own job description.
const PARTNER_LABEL_ES = {
  customer: "Cliente",
  guest: "Cliente / huésped",
  patient: "Paciente",
  caller: "Persona que llama",
  colleague: "Compañero de trabajo",
  supervisor: "Supervisor",
  vendor: "Proveedor",
  resident: "Residente",
  visitor: "Visitante",
  driver: "Conductor / transportista",
  passenger: "Pasajero",
};
const PARTNER_LABEL_EN = {
  customer: "Customer",
  guest: "Customer / guest",
  patient: "Patient",
  caller: "Caller",
  colleague: "Coworker",
  supervisor: "Supervisor",
  vendor: "Vendor",
  resident: "Resident",
  visitor: "Visitor",
  driver: "Driver",
  passenger: "Passenger",
};

export default function Briefing() {
  const { scenarioId } = useParams();
  const [searchParams] = useSearchParams();
  const isRemixMode = searchParams.get("mode") === "remix";
  const { lang, tr } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [starting, setStarting] = useState(false);
  const [sessionLevel, setSessionLevel] = useState(null); // null => use scenario/user default
  const [showFacts, setShowFacts] = useState(false);

  useEffect(() => {
    getScenario(scenarioId)
      .then((s) => {
        setScenario(s);
        // Default the picker to the scenario's own difficulty; learner can pick another for a NEW attempt only.
        setSessionLevel(s?.difficulty || user?.level || "basic");
      })
      .catch(() => {
        toast.error(tr("errorGeneric"));
        navigate("/dashboard");
      });
    // eslint-disable-next-line
  }, [scenarioId]);

  const start = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const session = await createSession(scenarioId, sessionLevel || undefined, isRemixMode);
      navigate(`/session/${session.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || tr("errorGeneric");
      if (status === 429) toast.error(detail);
      else if (status === 503) toast.error(tr("aiUnavailable"));
      else toast.error(detail);
    } finally {
      setStarting(false);
    }
  };

  if (!scenario) return <AppShell hideNav={!user} />;

  const partnerKey = scenario.partner || "customer";
  const learnerRole =
    lang === "en"
      ? scenario.learner_role_en || scenario.role_label_en || tr("briefingLearnerRoleFallback")
      : scenario.learner_role_es || scenario.role_label_es || tr("briefingLearnerRoleFallback");
  const partnerRole =
    lang === "en"
      ? PARTNER_LABEL_EN[partnerKey] || PARTNER_LABEL_EN.customer
      : PARTNER_LABEL_ES[partnerKey] || PARTNER_LABEL_ES.customer;
  const facts = scenario.facts || [];

  return (
    <AppShell hideNav={!user}>
      <section className="px-5 pt-4 pb-10 space-y-5" data-testid="briefing-screen">
        <button
          type="button"
          onClick={() => navigate(user ? "/dashboard" : "/")}
          className="wpe-ghost text-[13px] -ml-2"
          data-testid="briefing-back-btn"
        >
          <ChevronLeft size={16} /> {tr("close")}
        </button>

        <div className="space-y-2">
          <span className="wpe-eyebrow" data-testid="briefing-situation-eyebrow">
            {tr("briefingSituationEyebrow")} · {lang === "en" ? scenario.category_en : scenario.category_es}
          </span>
          <h1 className="text-[22px] font-bold leading-snug" data-testid="briefing-title">
            {lang === "en" ? scenario.title_en : scenario.title_es}
          </h1>
          {isRemixMode && (
            <div
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] text-[11px] font-semibold"
              data-testid="briefing-remix-badge"
            >
              <Repeat size={12} /> {tr("briefingRemixBadge")}
            </div>
          )}
          <p className="text-[13px] text-[rgb(var(--wpe-slate))]" data-testid="briefing-situation-text">
            {lang === "en" ? scenario.context_en : scenario.context_es}
          </p>
          {isRemixMode && (
            <p
              className="text-[11px] text-[rgb(var(--wpe-muted))]"
              data-testid="briefing-remix-hint"
            >
              {tr("briefingRemixHint")}
            </p>
          )}
        </div>

        {/* Simplified role summary — replaces the previous goal/facts/checkpoints/phrases stack. */}
        <div className="wpe-card space-y-1.5" data-testid="briefing-roles-card">
          <div className="text-[13px] text-[rgb(var(--wpe-navy))]">
            <span className="font-semibold">{tr("briefingYouAre")}</span>{" "}
            <span data-testid="briefing-learner-role">{learnerRole}</span>
          </div>
          <div className="text-[13px] text-[rgb(var(--wpe-navy))]">
            <span className="font-semibold">{tr("briefingAiRole")}</span>{" "}
            <span data-testid="briefing-partner-role">{partnerRole}</span>
          </div>
        </div>

        {/* Per-attempt difficulty picker — does NOT change saved profile. Hidden in remix mode. */}
        {!isRemixMode && (
        <div className="wpe-card space-y-2" data-testid="briefing-level-picker">
          <div className="flex items-center gap-2">
            <Gauge size={14} className="text-[rgb(var(--wpe-teal))]" />
            <span className="wpe-eyebrow">{tr("briefingLevelLabel")}</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {LEVELS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setSessionLevel(d)}
                className={`text-[11px] px-3 py-1 rounded-full border ${sessionLevel === d ? "bg-[rgb(var(--wpe-teal))] text-white border-[rgb(var(--wpe-teal))]" : "bg-white text-[rgb(var(--wpe-slate))] border-[rgb(var(--wpe-border))]"}`}
                data-testid={`briefing-level-${d}`}
              >
                {tr(d === "basic" ? "levelBasic" : d === "intermediate" ? "levelInter" : "levelAdvanced")}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[rgb(var(--wpe-muted))]" data-testid="briefing-level-hint">
            {tr("briefingLevelHint")}
          </p>
        </div>
        )}

        {/* Optional disclosure: fixed facts the learner already knows. Collapsed by default. */}
        {facts.length > 0 && (
          <div className="wpe-card space-y-2" data-testid="briefing-facts-card">
            <button
              type="button"
              onClick={() => setShowFacts((v) => !v)}
              className="w-full flex items-center justify-between text-left"
              aria-expanded={showFacts}
              data-testid="briefing-facts-toggle-btn"
            >
              <span className="wpe-eyebrow">{tr("briefingInfoYouKnow")}</span>
              {showFacts ? (
                <ChevronDown size={16} className="text-[rgb(var(--wpe-muted))]" />
              ) : (
                <ChevronRight size={16} className="text-[rgb(var(--wpe-muted))]" />
              )}
            </button>
            {showFacts && (
              <ul className="space-y-1.5 pl-1" data-testid="briefing-facts-list">
                {facts.map((f, i) => (
                  <li key={i} className="text-[13px] text-[rgb(var(--wpe-slate))] flex gap-2">
                    <span className="text-[rgb(var(--wpe-teal))] mt-0.5">•</span>
                    {lang === "en" ? f.en : f.es}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!user && (
          <div className="wpe-hint" data-testid="guest-note">
            {tr("guestNote")}
          </div>
        )}

        <PilotDisclaimer />

        <button
          type="button"
          onClick={start}
          disabled={starting}
          className="wpe-cta w-full"
          data-testid="start-scenario-btn"
        >
          <Play size={16} /> {starting ? tr("processing") : tr("startConvo")}
        </button>
      </section>
    </AppShell>
  );
}
